import { useState, useRef } from 'react';
import { Upload, X, FileText, Image as ImageIcon, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

interface DocumentUploadProps {
  maintenanceLogId?: string;
  onUploadComplete?: (documentId: string) => void;
  onDocumentsChange?: () => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
}

export function DocumentUpload({ maintenanceLogId, onUploadComplete, onDocumentsChange }: DocumentUploadProps) {
  const toast = useToast();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [documentType, setDocumentType] = useState('Other');
  const [description, setDescription] = useState('');

  const documentTypes = [
    'Invoice',
    'Receipt',
    'Work Order',
    'Inspection Report',
    'Photo - Before',
    'Photo - After',
    'Photo - Damage',
    'Other',
  ];

  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
    'image/heif',
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      // Check file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type. Only PDF and images are allowed.`);
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large. Maximum size is 10MB.`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    if (validFiles.length > 0) {
      const newUploadingFiles = validFiles.map(file => ({
        file,
        progress: 0,
      }));
      setUploadingFiles(prev => [...prev, ...newUploadingFiles]);
      validFiles.forEach(file => uploadFile(file));
    }
  };

  const uploadFile = async (file: File) => {
    try {
      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = maintenanceLogId 
        ? `${maintenanceLogId}/${fileName}`
        : `temp/${fileName}`;

      // Determine file type
      const fileType = file.type.startsWith('image/') ? 'image' : 'pdf';

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('maintenance-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Update progress
      setUploadingFiles(prev =>
        prev.map(f => f.file === file ? { ...f, progress: 50 } : f)
      );

      // Save to database if we have a maintenance log ID
      if (maintenanceLogId) {
        const { data, error: dbError } = await supabase
          .from('maintenance_documents')
          .insert({
            maintenance_log_id: maintenanceLogId,
            file_name: file.name,
            file_type: fileType,
            mime_type: file.type,
            file_size: file.size,
            storage_path: filePath,
            document_type: documentType,
            description: description || null,
            uploaded_by: user?.id,
          })
          .select()
          .single();

        if (dbError) throw dbError;

        // Update progress to 100%
        setUploadingFiles(prev =>
          prev.map(f => f.file === file ? { ...f, progress: 100 } : f)
        );

        // Remove from uploading list after a delay
        setTimeout(() => {
          setUploadingFiles(prev => prev.filter(f => f.file !== file));
        }, 1000);

        toast.success(`${file.name} uploaded successfully`);
        if (onUploadComplete) onUploadComplete(data.id);
        if (onDocumentsChange) onDocumentsChange();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadingFiles(prev =>
        prev.map(f => f.file === file ? { ...f, error: 'Upload failed' } : f)
      );
      toast.error(`Failed to upload ${file.name}`);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeUploadingFile = (file: File) => {
    setUploadingFiles(prev => prev.filter(f => f.file !== file));
  };

  return (
    <div className="space-y-4">
      {/* Document Type and Description */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Document Type
          </label>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {documentTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Description (Optional)
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief description..."
          />
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
      >
        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-lg font-semibold text-slate-700 mb-2">
          Drag & Drop Files Here
        </p>
        <p className="text-sm text-slate-500 mb-2">or click to browse</p>
        <p className="text-xs text-slate-400">
          PDF, JPG, PNG, GIF, WebP (Max 10MB per file)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Uploading...</h4>
          {uploadingFiles.map((uploadingFile, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
            >
              {uploadingFile.file.type.startsWith('image/') ? (
                <ImageIcon className="w-8 h-8 text-blue-600 flex-shrink-0" />
              ) : (
                <FileText className="w-8 h-8 text-red-600 flex-shrink-0" />
              )}
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {uploadingFile.file.name}
                </p>
                <p className="text-xs text-slate-500">
                  {(uploadingFile.file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                
                {/* Progress Bar */}
                {!uploadingFile.error && (
                  <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadingFile.progress}%` }}
                    ></div>
                  </div>
                )}
                
                {uploadingFile.error && (
                  <p className="text-xs text-red-600 mt-1">{uploadingFile.error}</p>
                )}
              </div>

              {uploadingFile.progress === 100 ? (
                <Loader className="w-5 h-5 text-green-600 animate-spin flex-shrink-0" />
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeUploadingFile(uploadingFile.file);
                  }}
                  className="p-1 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
